import React, { useState, useRef, useLayoutEffect, useEffect, useId, KeyboardEvent, useMemo, forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { DropdownPanel, DropdownOption } from './DropdownPanel';

export interface SelectFieldProps {
  id?: string;
  name: string;
  value: string | number;
  options: DropdownOption[];
  onChange: (value: string | number) => void;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  label?: string;
  labelKey?: string;
  labelClassName?: string;
  placeholder?: string;
  placeholderKey?: string;
  leadingIcon?: React.ReactNode;
  leadingIconContainerClassName?: string;
  descriptionText?: string;
  descriptionTextKey?: string;
  descriptionClassName?: string;
  errorText?: string;
  errorTextKey?: string;
  errorClassName?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  optionClassName?: string;
  containerClassName?: string;
  portalContainer?: HTMLElement | null;
}

export const SelectField = forwardRef<HTMLButtonElement, SelectFieldProps>(({
  id,
  name,
  value,
  options,
  onChange,
  onBlur,
  onFocus,
  label,
  labelKey,
  labelClassName,
  placeholder,
  placeholderKey,
  leadingIcon,
  leadingIconContainerClassName,
  descriptionText,
  descriptionTextKey,
  descriptionClassName,
  errorText,
  errorTextKey,
  errorClassName,
  fullWidth = false,
  disabled = false,
  required = false,
  className,
  triggerClassName,
  dropdownClassName,
  optionClassName,
  containerClassName,
  portalContainer,
}, ref) => {
  const generatedId = useId();
  const uniqueId = id || generatedId;
  const listboxId = `${uniqueId}-listbox`;
  const labelId = `${uniqueId}-label`;
  const triggerId = `${uniqueId}-trigger`;
  const descriptionId = `${uniqueId}-description`;
  const errorId = `${uniqueId}-error`;

  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const internalTriggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const setRefs = (node: HTMLButtonElement) => {
    (internalTriggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    }
  };

  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});

  const resolvedLabel = labelKey ? t(labelKey) : label;
  const resolvedDescription = descriptionTextKey ? t(descriptionTextKey) : descriptionText;
  const resolvedError = errorTextKey ? t(errorTextKey) : errorText;
  const hasError = !!resolvedError;

  const selectedOption = useMemo(() => options.find(opt => opt.value === value), [options, value]);
  const resolvedPlaceholder = placeholderKey ? t(placeholderKey) : placeholder;
  const displayLabel = selectedOption?.label || resolvedPlaceholder || 'Select...';
  const describedBy = hasError
    ? errorId
    : resolvedDescription
      ? descriptionId
      : undefined;

  const setInitialPosition = useCallback(() => {
    const el = internalTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownStyles({
      position: 'fixed',
      top: Math.round(rect.bottom + 6),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      zIndex: 1001,
    });
  }, []);

  const clampAndFlipPosition = useCallback(() => {
    const trigger = internalTriggerRef.current;
    const panel = dropdownRef.current;
    if (!isOpen || !trigger || !panel) return;

    const rect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();

    let top = rect.bottom + 6;
    let left = rect.left;

    const availableBelow = window.innerHeight - rect.bottom - 6;
    const availableAbove = rect.top - 6;

    if (panelRect.height > availableBelow && availableAbove > panelRect.height) {
      top = Math.max(8, rect.top - panelRect.height - 6);
    }

    if (left + panelRect.width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - panelRect.width - 8);
    }
    if (left < 8) left = 8;

    setDropdownStyles({
      position: 'fixed',
      top: Math.round(top),
      left: Math.round(left),
      width: Math.round(rect.width),
      zIndex: 1001,
    });
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    // First refine immediately on mount cycle
    clampAndFlipPosition();

    const handle = () => clampAndFlipPosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [clampAndFlipPosition, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(null);
      return;
    }
    const currentValIndex = options.findIndex(opt => opt.value === value && !opt.disabled);
    const firstEnabledIndex = options.findIndex(opt => !opt.disabled);
    const newIndex = currentValIndex !== -1 ? currentValIndex : firstEnabledIndex !== -1 ? firstEnabledIndex : null;
    setActiveIndex(prevIndex => (prevIndex !== newIndex ? newIndex : prevIndex));
  }, [isOpen, options, value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        internalTriggerRef.current && !internalTriggerRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOptionClick = (optionValue: string | number) => {
    if (disabled) return;
    onChange(optionValue);
    setIsOpen(false);
    internalTriggerRef.current?.blur();
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    setIsOpen(prev => {
      const opening = !prev;
      if (opening) setInitialPosition();
      return opening;
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const openKeys = ['Enter', ' ', 'ArrowDown', 'ArrowUp'];
    const closeKeys = ['Escape'];
    const navigationKeys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];

    if (!isOpen && openKeys.includes(event.key)) {
      event.preventDefault();
      setInitialPosition();
      setIsOpen(true);
      return;
    }

    if (isOpen && closeKeys.includes(event.key)) {
      event.preventDefault();
      setIsOpen(false);
      internalTriggerRef.current?.focus();
      return;
    }

    if (isOpen && navigationKeys.includes(event.key)) {
      event.preventDefault();
      const enabledOptions = options.map((opt, idx) => ({ ...opt, originalIndex: idx })).filter(opt => !opt.disabled);
      if (!enabledOptions.length) return;
      let currentEnabledIndex = activeIndex !== null ? enabledOptions.findIndex(opt => opt.originalIndex === activeIndex) : -1;

      switch (event.key) {
        case 'ArrowDown': currentEnabledIndex = currentEnabledIndex === -1 ? 0 : (currentEnabledIndex + 1) % enabledOptions.length; break;
        case 'ArrowUp': currentEnabledIndex = currentEnabledIndex <= 0 ? enabledOptions.length - 1 : currentEnabledIndex - 1; break;
        case 'Home': currentEnabledIndex = 0; break;
        case 'End': currentEnabledIndex = enabledOptions.length - 1; break;
      }
      setActiveIndex(enabledOptions[currentEnabledIndex].originalIndex);
      const optionElement = document.getElementById(`${uniqueId}-option-${enabledOptions[currentEnabledIndex].originalIndex}`);
      optionElement?.scrollIntoView({ block: 'nearest' });
      return;
    }

    if (isOpen && event.key === 'Enter' && activeIndex !== null) {
      event.preventDefault();
      const selectedOpt = options[activeIndex];
      if (selectedOpt && !selectedOpt.disabled) {
        handleOptionClick(selectedOpt.value);
        internalTriggerRef.current?.focus();
      }
    }
  };

  const getActiveDescendant = () => (isOpen && activeIndex !== null) ? `${uniqueId}-option-${activeIndex}` : undefined;
  const hasContent = value !== '' && value !== undefined && value !== null;

  const dropdownJsx = (
    <DropdownPanel
      ref={dropdownRef}
      isOpen={isOpen}
      options={options}
      value={value}
      activeIndex={activeIndex}
      onOptionClick={handleOptionClick}
      onOptionMouseEnter={setActiveIndex}
      listboxId={listboxId}
      labelId={resolvedLabel ? labelId : undefined}
      uniqueIdPrefix={uniqueId}
      className={dropdownClassName}
      optionClassName={optionClassName}
      popperStyles={dropdownStyles}
      popperAttributes={undefined}
      isMultiSelect={false}
      portalContainer={portalContainer}
    />
  );

  return (
    <div className={clsx('flex flex-col', fullWidth ? 'w-full' : 'w-auto', className)}>
      {resolvedLabel && (
        <label onClick={() => internalTriggerRef.current?.focus()} id={labelId} className={clsx(
          'block text-sm font-semibold mb-1.5 cursor-pointer',
          // Neobrutalism
          'text-slate-700',
          // Liquid glass
          '[.theme-liquid-glass_&]:text-white/90',
          disabled && 'opacity-70 cursor-not-allowed', hasError && 'text-red-600 [.theme-liquid-glass_&]:text-rose-400', labelClassName
        )}>
          {resolvedLabel}
          {required && <span className={clsx("ml-1", hasError ? "text-red-500" : "text-slate-500 [.theme-liquid-glass_&]:text-white/50")}>*</span>}
        </label>
      )}

      <div className={clsx('relative group', containerClassName)}>
        {!disabled && (
          <div className={clsx(
            'absolute inset-0 rounded-lg transition-transform duration-150 ease-out',
            // Neobrutalism shadow element
            'bg-black',
            // Liquid glass - hide shadow element
            '[.theme-liquid-glass_&]:hidden',
            isOpen ? 'translate-x-0.5 translate-y-0.5' : 'group-hover:translate-x-0.5 group-hover:translate-y-0.5 group-focus-within:translate-x-0.5 group-focus-within:translate-y-0.5'
          )} aria-hidden="true" />
        )}
        <div className={clsx(
          'relative flex items-center rounded-lg min-h-[46px]',
          // Neobrutalism
          'bg-white border-2',
          disabled ? 'border-slate-300 bg-slate-100 cursor-not-allowed' : hasError ? 'border-red-500' : 'border-black',
          !disabled && 'transition-transform duration-150 ease-out',
          !disabled && (isOpen ? '-translate-x-0.5 -translate-y-0.5' : 'group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-focus-within:-translate-x-0.5 group-focus-within:-translate-y-0.5'),
          // Liquid glass overrides
          '[.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
          '[.theme-liquid-glass_&]:hover:bg-white/10',
          disabled && '[.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:opacity-50',
          hasError && '[.theme-liquid-glass_&]:border-rose-400'
        )}>
          {leadingIcon && (
            <div className={clsx('absolute left-0 top-0 bottom-0 flex items-center pl-3 pointer-events-none z-10', disabled && 'opacity-50', leadingIconContainerClassName)}>
              {React.cloneElement(leadingIcon as React.ReactElement, {
                className: clsx(
                  'h-5 w-5',
                  hasError
                    ? 'text-red-500 [.theme-liquid-glass_&]:text-rose-400'
                    : 'text-slate-400 [.theme-liquid-glass_&]:text-white/50',
                  (leadingIcon as React.ReactElement).props.className
                )
              })}
            </div>
          )}
          <button
            ref={setRefs}
            id={triggerId}
            type="button"
            onClick={handleTriggerClick}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            onFocus={onFocus}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            aria-labelledby={resolvedLabel ? `${labelId} ${triggerId}` : triggerId}
            aria-activedescendant={getActiveDescendant()}
            aria-describedby={describedBy}
            aria-invalid={hasError ? 'true' : 'false'}
            aria-required={required}
            className={clsx(
              'block w-full text-left appearance-none bg-transparent rounded-lg py-2.5 pr-10',
              leadingIcon ? 'pl-10' : 'pl-3.5',
              'font-medium focus:outline-none',
              disabled
                ? 'cursor-not-allowed text-slate-500 [.theme-liquid-glass_&]:text-white/40'
                : 'cursor-pointer text-slate-800 [.theme-liquid-glass_&]:text-white/80',
              !hasContent && 'text-slate-400 [.theme-liquid-glass_&]:text-white/50',
              triggerClassName
            )}
          >
            {displayLabel}
          </button>
          <div className={clsx('absolute right-0 top-0 bottom-0 flex items-center pr-3 pointer-events-none', disabled && 'opacity-50')}>
            <ChevronDown
              className={clsx(
                'h-5 w-5 transition-transform duration-200',
                isOpen && 'transform rotate-180',
                hasError
                  ? 'text-red-500 [.theme-liquid-glass_&]:text-rose-400'
                  : 'text-slate-400 [.theme-liquid-glass_&]:text-white/50'
              )}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      <select
        tabIndex={-1}
        aria-hidden="true"
        name={name}
        value={hasContent ? String(value) : ''}
        onChange={() => {}}
        required={required}
        disabled={disabled}
        className="absolute h-px w-px opacity-0 pointer-events-none"
      >
        <option value="">
          {resolvedPlaceholder || 'Select...'}
        </option>
        {options.map(option => (
          <option key={option.value} value={String(option.value)} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>

      {isOpen && dropdownJsx}

      {resolvedDescription && !hasError && (
        <p id={descriptionId} className={clsx('mt-1.5 text-xs text-slate-500', descriptionClassName)}>
          {resolvedDescription}
        </p>
      )}
      {hasError && (
        <p id={errorId} className={clsx('mt-1.5 text-xs text-red-600 font-medium', errorClassName)}>
          {resolvedError}
        </p>
      )}
    </div>
  );
});

SelectField.displayName = 'SelectField';
